import {Component, effect, inject, input, signal, computed, ChangeDetectionStrategy} from "@angular/core"
import {
    TemplatesReferencedBySectionItemsForTemplateGQL,
    TemplatesReferencesSectionItemsForTemplateGQL,
    TemplatesSectionItemFragment,
} from "@app/platform/components/details/templates-section/templates-section.generated"
import {IsDefined} from "@cm/utils"
import {EntityCardComponent} from "@common/components/entity/entity-card/entity-card.component"
import {SectionComponent} from "@common/components/item"
import {PlaceholderComponent} from "@common/components/placeholders/placeholder/placeholder.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {Labels} from "@labels"
import {TemplatesSectionItemsForMaterialGQL} from "@platform/components/details/templates-section/templates-section.generated"

type ItemType = {id: string; __typename: "Material"} | {id: string; __typename: "Template"} | null | undefined
type TemplateItemView = "referencedBy" | "references"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-templates-section",
    imports: [SectionComponent, EntityCardComponent, PlaceholderComponent],
    templateUrl: "./templates-section.component.html",
    styleUrl: "./templates-section.component.scss",
})
export class TemplatesSectionComponent {
    readonly $item = input<ItemType>(null, {alias: "item"})
    readonly $templateItemView = input<TemplateItemView>("referencedBy", {alias: "templateItemView"})
    readonly $title = input<string | null | undefined>(null, {alias: "title"})

    readonly permission = inject(PermissionsService)
    readonly itemsForMaterialGql = inject(TemplatesSectionItemsForMaterialGQL)
    readonly itemsReferencedByForTemplateGql = inject(TemplatesReferencedBySectionItemsForTemplateGQL)
    readonly itemsReferencesForTemplateGql = inject(TemplatesReferencesSectionItemsForTemplateGQL)
    $can = this.permission.$to

    readonly $templates = signal<TemplatesSectionItemFragment[] | undefined | null>(undefined)

    constructor() {
        effect(() => this.loadTemplates(this.$item()))
    }

    loadTemplates(item: ItemType) {
        this.$templates.set(undefined)
        switch (item?.__typename) {
            case "Material": {
                const materialId = this.$item()?.id
                if (!materialId) return
                fetchThrowingErrors(this.itemsForMaterialGql)({materialId}, {errorPolicy: "all"})
                    .then(({material}) => {
                        // use map to prevent duplicates
                        const templatesById = new Map<string, TemplatesSectionItemFragment>()
                        material.revisions.forEach((revision) =>
                            revision.templateRevisions.filter(IsDefined).forEach((templateRevision) => {
                                if (templateRevision.template) templatesById.set(templateRevision.template.id, templateRevision.template)
                            }),
                        )
                        // sort by legacyId to ensure consistent order
                        this.$templates.set(Array.from([...templatesById.values()].sort((a, b) => a.legacyId - b.legacyId)))
                    })
                    .catch(() => {
                        this.$templates.set(null)
                    })
                break
            }
            case "Template": {
                const templateId = item!.id
                if (this.$templateItemView() === "referencedBy") {
                    fetchThrowingErrors(this.itemsReferencedByForTemplateGql)({templateId})
                        .then(({template}) => {
                            // use map to prevent duplicates
                            const templatesById = new Map<string, TemplatesSectionItemFragment>()
                            template.revisions.forEach((revision) =>
                                revision.referencedBy.forEach((templateRevision) => {
                                    if (templateRevision.template) templatesById.set(templateRevision.template.id, templateRevision.template)
                                }),
                            )
                            // sort by legacyId to ensure consistent order
                            this.$templates.set(Array.from([...templatesById.values()].sort((a, b) => a.legacyId - b.legacyId)))
                        })
                        .catch(() => {
                            this.$templates.set(null)
                        })
                } else {
                    fetchThrowingErrors(this.itemsReferencesForTemplateGql)({templateId})
                        .then(({template}) => {
                            // use map to prevent duplicates
                            const templatesById = new Map<string, TemplatesSectionItemFragment>()
                            template.revisions.forEach((revision) =>
                                revision.references.forEach((templateRevision) => {
                                    if (templateRevision.template) templatesById.set(templateRevision.template.id, templateRevision.template)
                                }),
                            )
                            // sort by legacyId to ensure consistent order
                            this.$templates.set(Array.from([...templatesById.values()].sort((a, b) => a.legacyId - b.legacyId)))
                        })
                        .catch(() => {
                            this.$templates.set(null)
                        })
                }
                break
            }
            default: {
                this.$templates.set(null)
            }
        }
    }

    thumbnailIds(template: TemplatesSectionItemFragment): string[] | null {
        if (template.galleryImage?.id) {
            return [template.galleryImage.id]
        }
        return template.latestRevision?.renderedImages?.map(({id}) => id) ?? null
    }

    readonly $showSection = computed(() => this.$templates() === undefined || !!this.$templates()?.length)

    readonly $showPlaceholders = computed(() => this.$templates() === undefined)

    protected readonly Labels = Labels
}
